// core
import React, { useEffect, useState } from "react";

// components
import LModal from "../LModal/LModal";
import ModalHeader from "../ModalHeader/ModalHeader";
import { getAboutInsurer } from "../../../../compare/api/api";
import IApiAboutInsurer from "../../../../compare/interfaces/api/IApiAboutInsurer";

import styles from "./AboutInsurerModal.module.scss";
import cx from "classnames";
import InsurerLogo from "../../InsurerLogo/InsurerLogo";
import useIsMounted from "../../../hooks/useIsMounted";
import useErrorLogger from "../../../hooks/useErrorLogger";

interface IAboutInsurerModal {
    providerId: number;
    isOpen: boolean;
    onClose: () => void;
}

const renderParagraph = (text: string, index: number): React.ReactNode => {
    if (index === 0) {
        // First sentence of the first paragraph is emphasized.
        const endOfFirstSentence = text.indexOf(".") + 1;
        const firstSentence = text.substring(0, endOfFirstSentence).trim();
        const followingSentence = text.substring(endOfFirstSentence, text.length).trim();

        return (
            <React.Fragment key={index}>
                <p className={cx(styles["about-paragraph"], "text-font-size-ml")}>
                    {firstSentence}
                </p>
                {followingSentence && (
                    <p className={styles["about-paragraph"]}>{followingSentence}</p>
                )}
            </React.Fragment>
        );
    }

    // We currently only have these two encoded chars in the the text data-set. (Southern Cross)
    // If we keep running into more, we could use a library such as the "he" library
    // https://github.com/mathiasbynens/he

    if (text.startsWith("&bull;&nbsp;")) {
        text = text.replace("&bull;&nbsp;", "");
        return (
            <ul key={index} className={styles["about-paragraph"]}>
                <li>{text}</li>
            </ul>
        );
    }

    return (
        <p key={index} className={styles["about-paragraph"]}>
            {text}
        </p>
    );
};

const AboutInsurerModal: React.FC<IAboutInsurerModal> = (props) => {
    const { providerId, isOpen, onClose } = props;

    const [insurerAbout, setInsurerAbout] = useState<IApiAboutInsurer | null>(null);
    const [isLoading, setLoading] = useState(false);

    const ErrorLogger = useErrorLogger();

    const isMounted = useIsMounted();

    useEffect(() => {
        if (providerId && isOpen) {
            setLoading(true);
            getAboutInsurer(providerId)
                .then((res) => {
                    setInsurerAbout(res);
                })
                .catch((err) => {
                    ErrorLogger.Log(err, {
                        source: "About Insurer Modal"
                    });
                    setInsurerAbout(null);
                })
                .finally(() => {
                    isMounted.current && setLoading(false);
                });
        }
    }, [providerId, isOpen]);

    return (
        <React.Fragment>
            <LModal
                Header={() => (
                    <ModalHeader
                        onClose={onClose}
                        title={`About ${insurerAbout?.fullName || "insurer"}`}
                    />
                )}
                calcHeightMode="auto"
                width="wide"
                isOpen={isOpen}
                onClose={onClose}
                isLoading={isLoading || !insurerAbout}>
                {() => {
                    const paragraphs = insurerAbout.informDescription
                        .split(/(<div[^>]*>[\s]*<\/div>|<br \/>|<\/p><p>)/gm)
                        .map((c) => c.replace(/<[^>]*>/gm, "").trim())
                        .filter((c) => c);

                    return (
                        <React.Fragment>
                            <div>
                                <div
                                    className={cx([
                                        styles["about-logo-container"],
                                        "gap-l"
                                    ])}>
                                    <InsurerLogo providerId={providerId} />
                                </div>
                                <div className="gap-l">
                                    {paragraphs.length > 0 &&
                                        paragraphs.map(renderParagraph)}

                                    <React.Fragment>
                                        {`${insurerAbout.fullName} has a${
                                            insurerAbout.financialRating.startsWith("A")
                                                ? "n"
                                                : ""
                                        } ${insurerAbout.financialRating} (${
                                            insurerAbout.ratingDescription
                                        }) financial strength rating given by ${
                                            insurerAbout.ratingAgency
                                        }.`}
                                    </React.Fragment>
                                    {insurerAbout.disclaimer && (
                                        <p
                                            className={cx(
                                                styles["about-disclaimer"],
                                                "text-light",
                                                "text-font-size-xs"
                                            )}>
                                            {insurerAbout.disclaimer}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                }}
            </LModal>
        </React.Fragment>
    );
};

export default AboutInsurerModal;
